import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AddAuthor.module.css";
import { ArrowLeft, Upload, X } from "lucide-react";
import { createAuthor } from "../../../api/admin/adminAuthors";

const AddAuthor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    language: 'english',
    slug: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'profile') {
        setProfileImage(file);
      } else {
        setCoverImage(file);
      }
    }
  };

  const removeImage = (type) => {
    if (type === 'profile') {
      setProfileImage(null);
    } else {
      setCoverImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('bio', formData.bio);
      formDataToSend.append('language', formData.language);
      if (formData.slug) {
        formDataToSend.append('slug', formData.slug);
      }
      
      // Add images
      if (profileImage) {
        formDataToSend.append('profileImage', profileImage);
      }
      if (coverImage) {
        formDataToSend.append('coverImage', coverImage);
      }

      await createAuthor(formDataToSend);
      navigate('/admin/dashboard', { state: { data: 'authors' } });
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to create author');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          className={styles.backButton}
          onClick={() => navigate('/admin/dashboard', { state: { data: 'authors' } })}
        >
          <ArrowLeft size={20} />
          Back to Authors
        </button>
        <h1>Add New Author</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="name">Author Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className={styles.input}
            placeholder="Enter author name"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="bio">Bio</label>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            className={styles.textarea}
            placeholder="Enter author bio"
            rows={4}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            className={styles.select}
          >
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="urdu">Urdu</option>
            <option value="punjabi">Punjabi</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="slug">Custom Slug (optional)</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            className={styles.input}
            placeholder="Leave empty to auto-generate from name"
          />
        </div>

        <div className={styles.imageUploads}>
          <div className={styles.imageGroup}>
            <label htmlFor="profileImage">Profile Image</label>
            <div className={styles.imageUpload}>
              {profileImage ? (
                <div className={styles.imagePreview}>
                  <img 
                    src={URL.createObjectURL(profileImage)} 
                    alt="Profile preview" 
                    className={styles.previewImage}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('profile')}
                    className={styles.removeImage}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label htmlFor="profileImage" className={styles.uploadArea}>
                  <Upload size={24} />
                  <span>Upload Profile Image</span>
                  <input
                    type="file"
                    id="profileImage"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'profile')}
                    className={styles.fileInput}
                  />
                </label>
              )}
            </div>
          </div>

          <div className={styles.imageGroup}>
            <label htmlFor="coverImage">Cover Image</label>
            <div className={styles.imageUpload}>
              {coverImage ? (
                <div className={styles.imagePreview}>
                  <img 
                    src={URL.createObjectURL(coverImage)} 
                    alt="Cover preview" 
                    className={styles.previewImage}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('cover')}
                    className={styles.removeImage}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label htmlFor="coverImage" className={styles.uploadArea}>
                  <Upload size={24} />
                  <span>Upload Cover Image</span>
                  <input
                    type="file"
                    id="coverImage"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'cover')}
                    className={styles.fileInput}
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard', { state: { data: 'authors' } })}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.name}
            className={styles.submitButton}
          >
            {isLoading ? 'Creating...' : 'Create Author'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAuthor;
